import {
    ConversationSpec,
    ConversationItem,
    ConversationDisplayItem,
    Block,
    isConversationDisplayItem
} from '../../entities/conversation'
import {
    Cursor,
    getNode,
    initialCursor,
    getNextCursorDownstream,
    findBlockCursor
} from './ConversationUtils'
import {
    sleep
} from '../../utils/async'

/**
 * This is responsible for managing the conversation state,
 * following branching, jumps, and lists
 *
 * It does not:
 * - Handle persistence - needs to be read from / written to
 * - Have any UI specific code
 * - Do text variable replacement - that can be done in UI
 */

interface Values {
    [key: string]: string
}

export interface State {
    spec: ConversationSpec // defines the conversation flow
    cursor: Cursor // the current position index in the spec
    node?: ConversationItem | Block // the current node pointed at by the cursor
    values: Values // any known values available for matching on 'for' items
    display: ConversationDisplayItem[] // a flat list of what to actually show the user
    finished?: true // don't do anything else
    inBlock?: Block // Parent block context
}

const TRANSITION = 600

export function initialState(spec: ConversationSpec, values: Values): State {
    return proceed({
        spec,
        cursor: [],
        values,
        display: []
    })
}

// proceed until next display item
export function proceed(state: State): State {

    const { spec, display, finished, inBlock } = state

    if (finished) { return state }

    const nextCursor = getNextCursor(state)
    if (!nextCursor) { return { ...state, finished: true } }

    const nextNode = getNode(spec, nextCursor)
    if (!nextNode) { return { ...state, finished: true } }

    if (isConversationDisplayItem(nextNode)) {

        const nextDisplay = display.concat([{ ...nextNode, status: 'new' }])

        return {
            ...state,
            cursor: nextCursor,
            node: nextNode,
            display: nextDisplay
        }
    }

    const nextBlock = nextNode.type === 'Block' ? nextNode : inBlock

    return proceed({
        ...state,
        cursor: nextCursor,
        node: nextNode,
        inBlock: nextBlock
    })
}

// extend the known values to be used in 'for' matching
export function setValues(state: State, values: Values): State {
    return {
        ...state,
        values: { ...state.values, ...values }
    }
}

// set status on display items
// leave previous - set to done
// enter next - set to active
// a generator provides the new state at each step
export async function *transitionDisplay(state: State, transitionSpeed = TRANSITION): AsyncIterableIterator<State> {

    const { display } = state

    let prevIndex = -1
    let nextIndex = -1

    const len = display.length

    if (len > 1) {
        prevIndex = len - 2
    }
    nextIndex = len - 1

    let nextState = state

    const setStatus = (index: number, status: string): State => {
        nextState = {
            ...nextState,
            display: nextState.display.map((item, idx) =>
                idx === index ? ({ ...item, status }) as ConversationDisplayItem : item
            )
        }
        return nextState
    }

    if (prevIndex > -1 && nextState.display[prevIndex].status !== 'done') {
        yield setStatus(prevIndex, 'leaving')
        await sleep(transitionSpeed)
        yield setStatus(prevIndex, 'done')
    }
    if (nextIndex > -1 && !nextState.finished) {
        yield setStatus(nextIndex, 'entering')
        await sleep(nextState.display[nextIndex].type === 'ReceiveOption' ? transitionSpeed / 4 : transitionSpeed)
        yield setStatus(nextIndex, 'active')
    }
}

// find the next cursor for the current state
export function getNextCursor(state: State): Cursor | undefined {

    const { spec, node, cursor, values, inBlock } = state

    if (cursor.length === 0) {
        return initialCursor(state.spec)
    }
    if (!node) {
        return
    }

    switch (node.type) {

        case 'For': {
            const found = values[node.match]
            const matchedBranch = node.cases[found]
            const defaultBranch = node.cases.default

            if (matchedBranch) { // move into matched branch - if nothing in branch, fall through
                const branchCursor = cursor.concat(['cases', found, 0])
                const nextNode = getNode(spec, branchCursor)
                if (nextNode) { return branchCursor }
            }

            if (defaultBranch) {
                const branchCursor = cursor.concat(['cases', 'default', 0])
                const nextNode = getNode(spec, branchCursor)
                if (nextNode) { return branchCursor }
            }
        }
        // couldn't match - fallthrough to next block

        case 'Say':
        case 'ReceiveOption':
        case 'ReceiveText': {
            // - can't fall through to a flow from anywhere
            // - can't fall through to master from a flow
            const nextCursor = getNextCursorDownstream(spec, cursor, node)
            const nextNode = nextCursor && getNode(spec, nextCursor)
            const currentFallThroughPrevented = inBlock && fallThroughPrevented(inBlock)
            const nextNodeFallThroughPrevented = nextNode && fallThroughPrevented(nextNode)
            if (currentFallThroughPrevented || nextNodeFallThroughPrevented) { return }
            return nextCursor
        }

        // can always jump explicitly to/from anywhere
        case 'Jump':
        case 'Block': {
            return findBlockCursor(spec, node.label)
        }
    }
}

function fallThroughPrevented(node: ConversationItem | Block) {
    return node.type === 'Block' && node.preventFallthrough
}
